import React, { useState, useEffect } from 'react';
import { Save, Info, Calculator, PoundSterling, Ruler, ArrowRight } from 'lucide-react';
import { cn } from '../utils/cn.js';
import toast from 'react-hot-toast';
import APIENDPOINT from './config.js';
export const PriceList = () => {

    const [pricing, setPricing] = useState({
        id: null,
        base_distance: 2.0,
        base_price: 6.0,
        price_per_mile: 2.60, // £1.30 per 0.5 mile = £2.60 per mile
        step_distance: 0.5
    });
    const [loading, setLoading] = useState(false);
    const [testMiles, setTestMiles] = useState(5);

    useEffect(() => {
        const fetchPricing = async () => {
            try {
                const res = await fetch(`${APIENDPOINT}booking.php?action=getPricing`);
                const data = await res.json();
                if (data.id) setPricing(data);
            } catch (err) { console.error("Fetch error:", err); }
        };
        fetchPricing();
    }, []);
    const handleSave = async () => {
        setLoading(true);
        const loadingToast = toast.loading("Saving pricing...");
        try {
            const res = await fetch(`${APIENDPOINT}booking.php?action=updatePricing`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(pricing)
            });
            if (res.ok) toast.success("Pricing updated!", { id: loadingToast });
            else throw new Error();
        } catch (err) {
            toast.error("Error saving.", { id: loadingToast });
        } finally { setLoading(false); }
    };
    const calculatePreview = () => {
        const miles = parseFloat(testMiles) || 0;
        const { base_distance, base_price, price_per_mile, step_distance } = pricing;

        // 1. Round UP to the nearest step (e.g., 2.1 becomes 2.5 if step is 0.5)
        const roundedMiles = Math.ceil(miles / step_distance) * step_distance;

        if (roundedMiles <= base_distance) return parseFloat(base_price).toFixed(2);

        // 2. Standard linear calculation
        const extraMiles = roundedMiles - base_distance;
        const totalFare = parseFloat(base_price) + (extraMiles * parseFloat(price_per_mile));

        return totalFare.toFixed(2);
    };


    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-24 md:pb-10">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Pricing Configuration</h2>
                        <p className="text-xs text-gray-500">Set base rates and per-mile charges</p>
                    </div>
                    <Info className="text-blue-400 w-5 h-5" />
                </div>

                <div className="p-6 space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <PriceInput
                            label="Base Distance (Miles)"
                            icon={Ruler}
                            value={pricing.base_distance}
                            onChange={(v) => setPricing({ ...pricing, base_distance: v })}
                            desc="The minimum distance included in the base price."
                        />
                        <PriceInput
                            label="Base Price (£)"
                            icon={PoundSterling}
                            value={pricing.base_price}
                            onChange={(v) => setPricing({ ...pricing, base_price: v })}
                            desc="The starting fare for any booking."
                        />
                        <PriceInput
                            label="Price Per Mile (£)"
                            icon={ArrowRight}
                            value={pricing.price_per_mile}
                            onChange={(v) => setPricing({ ...pricing, price_per_mile: v })}
                            desc="Additional cost for every mile after base distance."
                        />
                        <PriceInput
                            label="Step Increment (Miles)"
                            icon={Info}
                            value={pricing.step_distance}
                            onChange={(v) => setPricing({ ...pricing, step_distance: v })}
                            desc="System steps (default 0.5 for rounding logic)."
                        />
                    </div>

                    <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100 space-y-4">
                        <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm">
                            <Calculator size={18} />
                            <span>Quick Tester</span>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                            <div className="flex-1">
                                <label className="text-[10px] uppercase font-bold text-emerald-600 block mb-1">Total Miles to test</label>
                                <input
                                    type="number"
                                     step="0.01" 
                                    value={testMiles}
                                    onChange={(e) => setTestMiles(e.target.value)}
                                    className="w-full bg-white border-0 rounded-xl px-4 py-2 text-lg font-bold outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>
                            <div className="text-center md:text-right">
                                <label className="text-[10px] uppercase font-bold text-emerald-600 block mb-1">Estimated Fare</label>
                                <span className="text-3xl font-black text-emerald-900">£{calculatePreview()}</span>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all disabled:opacity-50"
                    >
                        {loading ? "Saving..." : <><Save size={20} /> Update Pricing</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Reusable Input Component
const PriceInput = ({ label, value, onChange, desc, icon: Icon }) => (
    <div className="space-y-2">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
            <Icon size={14} className="text-gray-400" />
            {label}
        </label>
        <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none font-bold text-gray-700 transition-all"
        />
        <p className="text-[10px] text-gray-400 leading-tight">{desc}</p>
    </div>
);